/**
 * Notifications — what sends which template, to whom (FR-K3).
 *
 * **Nothing ships switched on** (constitution §5), and a shipped binding that
 * is off says why on its own row, next to its switch — not in a release note
 * nobody reads. **Two audiences cannot be reached** (`assignee`, `reviewer`:
 * spec §9-20, §9-24), and the row says so before a firm switches it on.
 */

import { Badge, EmptyState, Switch } from '@fiveox/ui'
import { useMemo } from 'react'

import { type BindingRecord, type Vocabulary, comms } from '../../lib/api/comms'
import { type Act, audienceLabel } from './shared'

export function BindingList({
  bindings,
  vocab,
  mayEdit,
  act,
  templateName,
}: {
  bindings: BindingRecord[]
  vocab: Vocabulary | null
  mayEdit: boolean
  act: Act
  templateName: (id: string) => string
}) {
  const unaddressable = useMemo(
    () => new Set((vocab?.audiences ?? []).filter((a) => !a.addressable).map((a) => a.code)),
    [vocab],
  )

  if (bindings.length === 0) {
    return <EmptyState variant="outline" title="Nothing is bound to an event yet" />
  }

  return (
    <ul className="border-border divide-border divide-y rounded-2xl border bg-card shadow-sm">
      {bindings.map((b) => (
        <li key={b.id} className="flex flex-wrap items-start justify-between gap-3 px-4 py-4 sm:px-5">
          <div className="min-w-0 flex-1">
            <p className="text-foreground text-sm font-medium">
              {b.event_label} → {templateName(b.template_id)}
            </p>
            <p className="text-muted-foreground mt-1 flex flex-wrap items-center gap-2 text-xs">
              To: {audienceLabel(b.audience, vocab)}
              {unaddressable.has(b.audience) && <Badge tone="warning">Nothing will reach them</Badge>}
            </p>
            {b.note && <p className="text-muted-foreground mt-2 text-xs">{b.note}</p>}
          </div>
          <div className="shrink-0">
            {mayEdit ? (
              <Switch
                checked={b.active}
                onChange={() => void act(() => comms.updateBinding(b.id, { active: !b.active }))}
                label={b.active ? 'On' : 'Off'}
                aria-label={`${b.event_label}: ${b.active ? 'switch off' : 'switch on'}`}
              />
            ) : b.active ? (
              <Badge tone="success">On</Badge>
            ) : (
              <Badge>Off</Badge>
            )}
          </div>
        </li>
      ))}
    </ul>
  )
}
